import type { ReactNode } from 'react'
import { LogOut, ShieldCheck } from 'lucide-react'

import { Icon } from '@/components/Icon'
import { LocaleSwitch } from '@/components/LocaleSwitch'
import { Wordmark } from '@/components/Wordmark'
import { Button } from '@/components/ui/button'
import { useSession } from '@/features/auth/SessionContext'
import { WorkspaceSwitcher } from '@/features/workspace/WorkspaceSwitcher'
import { useI18n } from '@/i18n'
import { ROUTES, Link } from '@/lib/router'
import { useRouteChrome } from '@/lib/routeChrome'
import { cn } from '@/lib/utils'

/**
 * The application shell: Velox-branded header, the workspace chrome, the
 * pending-locale notice, and the standing disclaimer in the footer. The
 * disclaimer is part of the frame, not a per-page decoration — "flag, don't
 * decide" has to be visible wherever the reader is.
 *
 * The workspace chrome is here rather than per screen so that the answer to
 * "which workspace am I in?" is in the same place on every surface, including
 * the upload and the report.
 *
 * `sessionRow` reserves the header's second row. The session arrives over the
 * network, so its chrome (switcher, founder link, address, sign-out) appears a
 * few hundred milliseconds after first paint — and it is wide enough to wrap
 * onto a row of its own, which used to push the whole document down by 36 px
 * at that moment. That single reflow was 0.41 of the CLS on every signed-in
 * surface. The row is therefore laid out from the first paint and merely fills
 * up later; `min-h-8` is the height of the controls that land in it, so an
 * empty row is exactly as tall as a full one. The two token surfaces
 * (`/anmelden`, `/einladung`) pass `false`: they are signed out by definition,
 * nothing will ever arrive there, and an empty reserved row would be a lie
 * about what is coming.
 */
export function AppShell({
  children,
  activeWorkspaceId = null,
  sessionRow = true,
}: {
  children: ReactNode
  activeWorkspaceId?: string | null
  sessionRow?: boolean
}) {
  const { t, isPending } = useI18n()
  const { status, session, signOut } = useSession()
  // Per-route <title>, focus on the new view, and a spoken "you are now on …".
  // See lib/routeChrome.ts for what was broken before it existed.
  const { mainRef, announcement } = useRouteChrome(t)

  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:rounded-sm focus:bg-ink focus:px-4 focus:py-2 focus:text-white"
      >
        {t('a11y.skipToContent')}
      </a>

      {/* Two rows, and on a phone they stay two rows.
       *
       * Measured before this change on a 390 x 390 CSS px viewport: the header
       * wrapped to FOUR rows and stood 240 px tall — over a quarter of the
       * viewport, sticky, on every scroll. Nothing was removed to fix it; four
       * pieces of redundancy were told to stand down below `sm`:
       *  · the brand attribution ("von Velox-KAi") — it is in the footer;
       *  · the tagline — it was already hidden below `sm` and now waits for
       *    `md`, where there is room for it next to the lockup;
       *  · the visible "Sprache" label of the locale switch — the control keeps
       *    its `aria-label`, so nothing is lost to a screen reader;
       *  · the signed-in address — `sr-only` below `sm`, i.e. still announced,
       *    just not occupying a row of a 390 px viewport.
       * The workspace switcher and the language control, the two the brief
       * names, are untouched at every width. */}
      <header className="sticky top-0 z-40 border-b border-border bg-white/95 backdrop-blur-sm">
        <div className="mx-auto max-w-[1200px] px-4 py-2 sm:px-6 sm:py-4">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <Link to={ROUTES.home} className="rounded-xs">
              <Wordmark />
            </Link>
            <span className="hidden text-[13px] text-muted md:inline">{t('app.tagline')}</span>
            <LocaleSwitch className="ms-auto" />
          </div>
          {sessionRow ? (
            <div className="mt-2 flex min-h-8 flex-wrap items-center justify-end gap-x-4 gap-y-2">
              {status === 'signedIn' ? (
                <>
                  <WorkspaceSwitcher activeId={activeWorkspaceId} />
                  {/* The founder dashboard. Shown only to a founder — a link that
                      answers 403 is worse than no link — and the server enforces
                      the same rule regardless of what is rendered here. */}
                  {session?.global_role === 'founder' ? (
                    <Link
                      to={ROUTES.admin}
                      className="inline-flex items-center gap-2 rounded-xs text-[13px] text-ink-2 underline underline-offset-2"
                      data-testid="nav-admin"
                    >
                      <Icon icon={ShieldCheck} />
                      <span className="max-sm:sr-only">{t('nav.admin')}</span>
                    </Link>
                  ) : null}
                  <span
                    className="sr-only text-[12px] text-muted sm:not-sr-only"
                    data-testid="session-email"
                  >
                    {session?.email}
                  </span>
                  <Button variant="ghost" size="sm" onClick={() => void signOut()}>
                    <Icon icon={LogOut} />
                    <span className="sr-only sm:not-sr-only">{t('auth.signOut')}</span>
                  </Button>
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      </header>

      {isPending ? (
        <div
          role="status"
          className="border-b border-border bg-signal-info-soft px-6 py-2 text-center text-[13px] text-signal-info"
        >
          {t('lang.frBanner')}
        </div>
      ) : null}

      {/* The route announcer. Outside <main> so that replacing the view does
          not replace the live region itself — a live region that is remounted
          with its new text is never announced. */}
      <p aria-live="polite" className="sr-only" data-testid="route-announcer">
        {announcement}
      </p>

      {/* `min-h`: the content area of a session surface is at least one
          viewport tall, so the footer starts below the fold and stays there
          while sections fill in. Without the floor the footer slides into view
          the moment a short intermediate state renders and out again when the
          data lands — two layout shifts per page, and the reader watches the
          page breathe. The floor binds on no real surface: the shortest one
          measured (the run progress at 1066 px total) already exceeds it. */}
      <main
        id="main"
        ref={mainRef}
        tabIndex={-1}
        className={cn(
          'mx-auto w-full max-w-[1200px] grow px-6 py-8 md:py-12',
          sessionRow && 'min-h-[calc(100dvh-10rem)]',
        )}
      >
        {children}
      </main>

      <footer className="border-t border-border bg-surface">
        <div className="mx-auto max-w-[1200px] px-6 py-6 text-[12px] leading-relaxed text-muted">
          <p className="max-w-[80ch]">{t('start.lead')}</p>
          <p className="mt-2">
            {t('app.name')} · {t('app.attribution')}
          </p>
        </div>
      </footer>
    </div>
  )
}
